import type { Action } from "../../../types/actions";
import { handle } from "redux-pack";
import type { Speaker } from "../../../types/working-model";
import {
	CREATE_SPEAKER,
	GET_SPEAKERS,
	SAVE_SPEAKER,
	UPDATE_SPEAKER
} from "../../actions/admin/speakers";

export interface SpeakersState {
	speakersById: Record<number, Speaker>;
	speakerIds: number[];
	speakersTotal: number;
	speakersPage: number;
	loadingSpeakers: boolean;
	speakersLoaded: boolean;
	speakersError: string;
	savingSpeakers: boolean;
	savingSpeakersError: string;
	creatingSpeaker: boolean;
	creatingSpeakerError: string;
}

const initialState: SpeakersState = {
	creatingSpeaker: false,
	creatingSpeakerError: "",
	loadingSpeakers: false,
	speakersLoaded: false,
	savingSpeakers: false,
	savingSpeakersError: "",
	speakersById: {},
	speakerIds: [],
	speakersTotal: 0,
	speakersPage: 1,
	speakersError: "",
};

const upsertSpeaker = (state: SpeakersState, speaker: Speaker): SpeakersState => {
	const known = state.speakersById[speaker.speaker] !== undefined;

	return {
		...state,
		speakersById: { ...state.speakersById, [speaker.speaker]: speaker },
		speakerIds: known ? state.speakerIds : [...state.speakerIds, speaker.speaker],
		speakersTotal: known ? state.speakersTotal : state.speakersTotal + 1
	};
};

export default function SpeakersReducer(
	state: SpeakersState = initialState,
	action: Action
): SpeakersState {
	switch (action.type) {
		case GET_SPEAKERS: {
			return handle(state, action, {
				start: (state: SpeakersState) => ({ ...state, loadingSpeakers: true }),
				failure: (state: SpeakersState) => ({
					...state,
					speakersError: "Unable to load speakers."
				}),
				success: (state: SpeakersState) => {
					const { results, total, page } = action.payload as { results: Speaker[]; total: number; page: number };

					const speakersById = { ...state.speakersById };
					for (const speaker of results) {
						speakersById[speaker.speaker] = speaker;
					}

					const alreadyListed = new Set(state.speakerIds);
					const pageIds = results.map((speaker: Speaker) => speaker.speaker);

					return {
						...state,
						speakersError: "",
						speakersById,
						speakerIds: page <= 1
							? pageIds
							: [...state.speakerIds, ...pageIds.filter((id: number) => !alreadyListed.has(id))],
						speakersTotal: total,
						speakersPage: page
					};
				},
				finish: (state: SpeakersState) => ({ ...state, loadingSpeakers: false, speakersLoaded: true })
			});
		}
		case CREATE_SPEAKER: {
			return upsertSpeaker(state, action.payload);
		}
		case UPDATE_SPEAKER: {
			return upsertSpeaker(state, action.payload);
		}
		case SAVE_SPEAKER: {
			return handle(state, action, {
				start: (state: SpeakersState) => ({ ...state, savingSpeakers: true }),
				failure: (state: SpeakersState) => ({
					...state,
					savingSpeakersError: "Unable to save speaker"
				}),
				success: (state: SpeakersState) => ({
					...upsertSpeaker(state, action.payload),
					savingSpeakersError: ""
				}),
				finish: (state: SpeakersState) => ({ ...state, savingSpeakers: false })
			});
		}
		default:
			return state;
	}
}
